import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMe } from "@/features/auth/useMe";
import { ApiError, getAdminTeams } from "@/lib/api";
import { hostKind } from "@/lib/hostKind";
import { unscopedKey } from "@/lib/queryClient";
import {
  canSetUpClub,
  isBootstrapTeam,
  newState,
  readSetup,
  setupKey,
  writeSetup,
  type OnboardingState,
} from "../onboardingState";

/** The same host/capability/club-wide cardinality decision owns entry and /welcome. */
export function useClubOnboarding() {
  const me = useMe();
  const client = useQueryClient();
  const allowed = canSetUpClub(me.data, hostKind());
  const key = setupKey(
    me.data?.user?.id ?? "",
    me.data?.current_club?.id ?? "",
  );
  const [stored, setStored] = useState(() => ({ key, value: readSetup(key) }));
  const state = stored.key === key ? stored.value : readSetup(key);
  useEffect(() => {
    const refresh = () => setStored({ key, value: readSetup(key) });
    refresh();
    const storage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) refresh();
    };
    const local = (event: Event) => {
      if ((event as CustomEvent<string>).detail === key) refresh();
    };
    window.addEventListener("storage", storage);
    window.addEventListener("tktic-club-setup", local);
    return () => {
      window.removeEventListener("storage", storage);
      window.removeEventListener("tktic-club-setup", local);
    };
  }, [key]);
  const update = useCallback(
    (
      patch:
        | Partial<OnboardingState>
        | ((s: OnboardingState) => OnboardingState),
    ) => {
      const previous = readSetup(key);
      writeSetup(
        key,
        typeof patch === "function"
          ? patch(previous)
          : { ...previous, ...patch },
      );
    },
    [key],
  );
  const teams = useQuery({
    queryKey: unscopedKey("club-onboarding-teams", { key }),
    queryFn: getAdminTeams,
    enabled: allowed,
    staleTime: 0,
    retry: false,
  });
  const realTeams = teams.data?.filter((t) => !isBootstrapTeam(t));
  const team = realTeams?.find((t) => t.id === state.teamId);
  // Storage can deliver an anchor before this tab's cached team list has
  // refreshed. Read the host-authorized list before discarding that anchor.
  useEffect(() => {
    if (
      !allowed ||
      !teams.isSuccess ||
      teams.isFetching ||
      !state.teamId ||
      team
    )
      return;
    let cancelled = false;
    const anchor = state.teamId;
    void getAdminTeams()
      .then((rows) => {
        if (cancelled || readSetup(key).teamId !== anchor) return;
        client.setQueryData(
          unscopedKey("club-onboarding-teams", { key }),
          rows,
        );
        if (!rows.some((row) => row.id === anchor && !isBootstrapTeam(row))) {
          update((s) => ({
            ...newState(),
            dismissed: s.dismissed,
            skippedWrites: s.skippedWrites,
          }));
        }
      })
      .catch(() => {
        // Surface an ordinary query error with its normal retry path.
        if (!cancelled) void teams.refetch();
      });
    return () => {
      cancelled = true;
    };
  }, [
    allowed,
    teams.isSuccess,
    teams.isFetching,
    state.teamId,
    team,
    key,
    client,
    update,
  ]);
  const loading =
    me.isLoading ||
    (allowed &&
      (teams.isPending ||
        !teams.data ||
        (!!state.teamId && !team && !teams.isError)));
  const anonymous = me.error instanceof ApiError && me.error.status === 401;
  const error = (me.isError && !anonymous) || (allowed && teams.isError);
  const empty =
    allowed && teams.isSuccess && !teams.isError && realTeams?.length === 0;
  const resumable = allowed && !teams.isError && !!team && !state.completed;
  const autoStart =
    empty &&
    !state.dismissed &&
    !state.completed &&
    !state.teamId &&
    !state.pending;
  return {
    me,
    anonymous,
    allowed,
    key,
    state,
    update,
    teams,
    realTeams,
    team,
    loading,
    error,
    empty,
    resumable,
    autoStart,
  };
}
